/**
 * Unified API Error class for consistent error handling across the application.
 *
 * Normalizes error responses from different API formats (FCC, Common Merchant, Tax Injector)
 * into a single, predictable structure.
 */

/**
 * Field-level validation error
 */
export interface FieldError {
  field: string
  message: string
  code?: string
}

/**
 * Details for constructing an ApiError
 */
export interface ApiErrorDetails {
  message: string
  code?: string
  statusCode?: number
  fieldErrors?: FieldError[]
  originalResponse?: unknown
  /** Correlation ID for tracing in DataDog */
  traceId?: string
  /** Timestamp when the error occurred */
  timestamp?: Date
  /** Request URL that caused the error */
  url?: string
  /** HTTP method of the failed request */
  method?: string
}

/**
 * Unified error class for all API errors.
 *
 * Provides helper methods to identify error types and format messages for display.
 */
export class ApiError extends Error {
  readonly code?: string
  readonly statusCode?: number
  readonly fieldErrors?: FieldError[]
  readonly originalResponse?: unknown
  readonly traceId?: string
  readonly timestamp: Date
  readonly url?: string
  readonly method?: string

  constructor(details: ApiErrorDetails) {
    super(details.message)
    this.name = 'ApiError'
    this.code = details.code
    this.statusCode = details.statusCode
    this.fieldErrors = details.fieldErrors
    this.originalResponse = details.originalResponse
    this.traceId = details.traceId
    this.timestamp = details.timestamp ?? new Date()
    this.url = details.url
    this.method = details.method

    // Maintains proper stack trace for where error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError)
    }
  }

  /**
   * Check if this is a network error (no response received)
   */
  isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR' || this.statusCode === undefined
  }

  /**
   * Check if this is a validation error (has field errors)
   */
  isValidationError(): boolean {
    return (
      this.code === 'VALIDATION_ERROR' ||
      (Array.isArray(this.fieldErrors) && this.fieldErrors.length > 0)
    )
  }

  /**
   * Check if this is an authentication error (401)
   */
  isAuthenticationError(): boolean {
    return this.statusCode === 401
  }

  /**
   * Check if this is an authorization error (403)
   */
  isAuthorizationError(): boolean {
    return this.statusCode === 403
  }

  /**
   * Check if this is a server error (5xx)
   */
  isServerError(): boolean {
    return this.statusCode !== undefined && this.statusCode >= 500 && this.statusCode < 600
  }

  /**
   * Check if this is a not found error (404)
   */
  isNotFoundError(): boolean {
    return this.statusCode === 404
  }

  /**
   * Format error for display in a toast notification.
   * Returns a user-friendly message.
   */
  toToastMessage(): string {
    // For validation errors with field errors, create a summary
    if (this.isValidationError() && this.fieldErrors && this.fieldErrors.length > 0) {
      const fieldMessages = this.fieldErrors
        .map(fe => `${fe.field}: ${fe.message}`)
        .slice(0, 3) // Limit to first 3 for toast brevity
        .join('\n')

      const remaining = this.fieldErrors.length - 3
      const suffix = remaining > 0 ? `\n...and ${remaining} more` : ''

      return `${this.message}\n${fieldMessages}${suffix}`
    }

    // For other errors, include code if available
    if (this.code && this.code !== this.message) {
      return `${this.message} (${this.code})`
    }

    return this.message
  }

  /**
   * Get detailed error information for the error details drawer.
   * Returns all relevant information in a structured format.
   */
  toDetailedInfo(): {
    message: string
    code?: string
    statusCode?: number
    traceId?: string
    timestamp: string
    url?: string
    method?: string
    fieldErrors?: FieldError[]
    errorType: string
  } {
    let errorType = 'Error'
    if (this.isNetworkError()) errorType = 'Network Error'
    else if (this.isValidationError()) errorType = 'Validation Error'
    else if (this.isAuthenticationError()) errorType = 'Authentication Error'
    else if (this.isAuthorizationError()) errorType = 'Authorization Error'
    else if (this.isServerError()) errorType = 'Server Error'
    else if (this.isNotFoundError()) errorType = 'Not Found'

    return {
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      traceId: this.traceId,
      timestamp: this.timestamp.toISOString(),
      url: this.url,
      method: this.method,
      fieldErrors: this.fieldErrors,
      errorType,
    }
  }

  /**
   * Type guard to check if an unknown error is an ApiError
   */
  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError
  }
}
